import { FEAT, SKILL_FX, formFx, type UniqueId } from '@somnhold/content';
import { cellAtWorld } from '../grid.ts';
import { cosA, dist2, iatan2, idiv, imin, mulBp, sinA, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Hero, UqDue, Zone } from '../types.ts';
import { damageEnemy, enemiesNear, ev, isFlying, targetable } from './combat.ts';
import { featPieceTest, featPieceWatch, featUse } from './feats.ts';
import { formFinal } from './forms.ts';
import { along, poison, slowIt, wallCells } from './skills.ts';
import { stunFor } from './towers.ts';
import { fx, hasUq, heavyStun } from './uniques.ts';

/**
 * The druid's panoply in a fight (docs/PANOPLIA.md §10), as battle/panoply.ts is the warrior's: the skills' programs call
 * these where a piece changes them (`special` steps with `when: { uq }`). What happens later goes in the same queue
 * (`s.uqDue`: the hive's pulses, the cracks' steps, the root's end); what a piece changes on the ground rides on the
 * zone itself (`z.uqz`, run by `druidZone` from updateZones), and on the summons (`b.uq`, by `druidBotHit`).
 */

/** The most nightmares a plague passes its poison on to in one pulse (docs/PANOPLIA.md §8: cascades with a cap). */
const SPREAD_PER_PULSE = 16;
/** The most cracks a quake opens (§8: the hero's jumps a tick). */
const CRACKS_MAX = 6;

const heroById = (s: BattleState, id: number | undefined): Hero | undefined => (id === undefined ? undefined : s.heroes.find((h) => h.id === id));

/** Puts a later effect in the queue (in tick order, then as it came), as battle/panoply.ts does. */
const due = (s: BattleState, d: UqDue): void => {
  const q = (s.uqDue ??= []);
  let i = q.length;
  while (i > 0 && (q[i - 1] as UqDue).at > d.at) i--;
  q.splice(i, 0, d);
};

/** A piece's stun (§8): as the saga's pieces stun, never the bosses nor the dragon. */
const pieceStun = (e: Enemy, t: number): number => (e.kind === 'dragon' ? 0 : heavyStun(e, t));

/** Held (clava, sujeta; §8): a full slow (halved on the big ones), and a flyer brought down; never the dragon. */
const hold = (e: Enemy, t: number): void => {
  if (e.kind === 'dragon') return;
  slowIt(e, 10000, t);
  if (isFlying(e) && (e.grounded ?? 0) < t) e.grounded = t;
};

const poisoned = (e: Enemy): boolean => e.dotTicks > 0 && (e.dotType ?? 'poison') === 'poison';
const live = (s: BattleState, e: Enemy): boolean => e.hp > 0 && targetable(s, e) && e.kind !== 'boneWall';

/** The last zone a program laid (the `zone` step pushes it at the end). */
const lastZone = (s: BattleState, h: Hero, kind: Zone['kind']): Zone | undefined => {
  const z = s.zones[s.zones.length - 1];
  return z && z.kind === kind && z.pid === h.pid ? z : undefined;
};

/* ---------- Bastón de la colmena (Enjambre) ---------- */

/** The swarm leaves a hive where it struck (`dmg`: the swarm's poison). Reina (its final form): it lasts longer, its wasps slow. */
export const hiveOn = (s: BattleState, h: Hero, e: Enemy | null, dmg: number, every: number, ticks: number): void => {
  if (!e || !hasUq(h, 'hiveStaff')) return;
  const f = fx('hiveStaff', h);
  const fin = formFinal(h, 'hiveStaff');
  const life = fin ? mulBp(f.ticks as number, formFx('hiveStaff', 'final').ticksBp as number) : (f.ticks as number);
  const pulses = idiv(life, f.every as number);
  if (pulses <= 0) return;
  due(s, { at: s.tick + (f.every as number), k: 'hivePulse', hid: h.id, x: e.x, y: e.y, n: [pulses - 1, mulBp(dmg, f.stingBp as number), every, ticks, fin ? 1 : 0], ids: [] });
  ev(s, { t: 'boom', x: e.x, y: e.y, r: f.radius as number, kind: 'hive' });
};

const hivePulse = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('hiveStaff', h);
  const [left = 0, dmg = 0, every = 6, ticks = 72, fin = 0] = d.n ?? [];
  const r = f.radius as number;
  const near = enemiesNear(s, d.x, d.y, r, (o) => live(s, o) && !poisoned(o));
  near.sort((a, b) => dist2(a.x, a.y, d.x, d.y) - dist2(b.x, b.y, d.x, d.y) || a.id - b.id);
  const ids = [...(d.ids ?? [])];
  const before = ids.length;
  const ff = fin ? formFx('hiveStaff', 'final') : null;
  let n = 0;
  for (const o of near.slice(0, f.n as number)) {
    poison(o, dmg, every, ticks, h.pid);
    if (ff) slowIt(o, ff.slowBp as number, ff.slowTicks as number);
    if (!ids.includes(o.id)) ids.push(o.id);
    ev(s, { t: 'beam', x: d.x, y: d.y, tx: o.x, ty: o.y, kind: 'hiveSting' });
    n++;
  }
  ev(s, { t: 'boom', x: d.x, y: d.y, r, kind: fin ? 'hivePulseQueen' : 'hivePulse' });
  featUse(s, h, 'hiveStaff', n);
  if (before < FEAT.need.hive && ids.length >= FEAT.need.hive) featPieceTest(s, h, 'hiveStaff');
  if (left > 0) due(s, { ...d, at: s.tick + (f.every as number), n: [left - 1, dmg, every, ticks, fin], ids });
};

/* ---------- Bastón del herbolario (Esporas) ---------- */

/** The spore cloud just laid drifts toward the biggest nightmare near it, poisoning `damageBp` as hard. */
export const driftOn = (s: BattleState, h: Hero): void => {
  if (!hasUq(h, 'herbalistStaff')) return;
  const z = lastZone(s, h, 'plague');
  if (!z) return;
  z.uqz = 'herbalistStaff';
  z.uqh = h.id;
  z.seen = [];
  z.uqT = z.ticks;
  z.damage = mulBp(z.damage, fx('herbalistStaff', h).damageBp as number);
};

const driftTick = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('herbalistStaff', h);
  let big: Enemy | null = null;
  for (const o of enemiesNear(s, z.x, z.y, f.seek as number, (e) => live(s, e))) {
    if (!big || o.maxHp > big.maxHp || (o.maxHp === big.maxHp && o.id < big.id)) big = o;
  }
  if (big && !withinRange(z.x, z.y, big.x, big.y, f.step as number)) {
    const to = along(z.x, z.y, big.x, big.y, f.step as number);
    z.x = to.x;
    z.y = to.y;
  }
  if (z.ticks % z.every === 0) seenIn(s, z, h, 'herbalistStaff', FEAT.need.cloud);
};

/** What the cloud poisoned this pulse, counted once each; the `need`-th is the piece's test. */
const seenIn = (s: BattleState, z: Zone, h: Hero, id: UniqueId, need: number): void => {
  const seen = (z.seen ??= []);
  const before = seen.length;
  for (const o of enemiesNear(s, z.x, z.y, z.radius, (e) => targetable(s, e))) if (!seen.includes(o.id)) seen.push(o.id);
  featUse(s, h, id, seen.length - before);
  if (before < need && seen.length >= need) featPieceTest(s, h, id);
};

/** Hongos (the staff's final form): where the cloud ends, it comes out once more, `damageBp` as hard (it drifts no more). */
const mushrooms = (s: BattleState, z: Zone, h: Hero): void => {
  if (!formFinal(h, 'herbalistStaff')) return;
  const bp = formFx('herbalistStaff', 'final').damageBp as number;
  s.zones.push({ id: s.nextId++, kind: 'plague', x: z.x, y: z.y, radius: z.radius, cells: [], ticks: z.uqT ?? 72, every: z.every, damage: mulBp(z.damage, bp), slowBp: z.slowBp, pid: z.pid });
  ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'mushrooms' });
};

/* ---------- Bastón de zarza (Látigo de zarza) ---------- */

/** What the lash drags stays thorned: its blows on the druid take back a share of the druid's attack. */
export const briarThorn = (s: BattleState, h: Hero, e: Enemy | null): void => {
  if (!e || !hasUq(h, 'briarStaff') || stunFor(e, 1) === 0 || e.kind === 'boneWall') return;
  e.briarBy = h.id;
  e.briarUntil = s.tick + (fx('briarStaff', h).ticks as number);
};

/** The lash done: dragging `need` or more is the piece's test; Seto (its final form) leaves a row of briars ahead. */
export const briarHedge = (s: BattleState, h: Hero, hits: Enemy[], aim: { x: number; y: number } | null): void => {
  if (!hasUq(h, 'briarStaff')) return;
  let dragged = 0;
  for (const o of hits) if (o.briarBy === h.id && (o.briarUntil ?? 0) > s.tick) dragged++;
  if (dragged >= FEAT.need.lash) featPieceTest(s, h, 'briarStaff');
  if (!aim || !formFinal(h, 'briarStaff')) return;
  const f = formFx('briarStaff', 'final');
  const p = along(h.x, h.y, aim.x, aim.y, f.ahead as number);
  const cells = wallCells(s, h, p, f.n as number);
  if (!cells.length) return;
  s.zones.push({ id: s.nextId++, kind: 'thorns', x: p.x, y: p.y, radius: 0, cells, ticks: f.ticks as number, every: 6, damage: 0, slowBp: f.slowBp as number, pid: h.pid });
  ev(s, { t: 'boom', x: p.x, y: p.y, r: 900, kind: 'hedge' });
};

/* ---------- Túnica de los emplastos (Raíz profunda) ---------- */

/** The rooted ally gives back what it takes; `ticks`: how long the root holds. */
export const rootBy = (s: BattleState, h: Hero, o: Hero | null, ticks: number): void => {
  if (!o || !hasUq(h, 'poulticeTunic')) return;
  o.rootBy = h.id;
  const foes = enemiesNear(s, o.x, o.y, FEAT.need.rootFoesR, (e) => live(s, e)).length;
  if (foes >= FEAT.need.rootFoes) featPieceWatch(s, h, 'poulticeTunic', [o.id], ticks, -1);
  if (formFinal(h, 'poulticeTunic')) due(s, { at: s.tick + ticks, k: 'rootEnd', hid: h.id, x: o.x, y: o.y, ids: [o.id] });
};

/** Brote (the tunic's final form): the root ended, a sprout mends around the ally. */
const rootEnd = (s: BattleState, h: Hero, d: UqDue): void => {
  const o = heroById(s, d.ids?.[0]);
  if (!o || o.downTicks > 0) return;
  const f = formFx('poulticeTunic', 'final');
  s.zones.push({ id: s.nextId++, kind: 'bloom', x: o.x, y: o.y, radius: f.radius as number, cells: [], ticks: f.ticks as number, every: f.every as number, damage: 0, slowBp: 0, heal: f.healBp as number, pid: h.pid });
  ev(s, { t: 'boom', x: o.x, y: o.y, r: f.radius as number, kind: 'sprout' });
};

/** A blow on a hero: rooted by a poultice tunic, it gives some back; struck by a thorned nightmare, the thorns answer. */
export const druidHurt = (s: BattleState, h: Hero, dmg: number, from: Enemy | null | undefined): void => {
  if (!from || from.hp <= 0) return;
  if (h.rootBy !== undefined) {
    const d = (h.rooted ?? 0) > 0 ? heroById(s, h.rootBy) : undefined;
    if (!d) h.rootBy = undefined;
    else {
      const back = damageEnemy(s, from, mulBp(dmg, fx('poulticeTunic', d).backBp as number), { pid: d.pid });
      featUse(s, d, 'poulticeTunic', back);
    }
  }
  if (from.briarBy === h.id && from.hp > 0) {
    if ((from.briarUntil ?? 0) <= s.tick) from.briarBy = undefined;
    else {
      const back = damageEnemy(s, from, mulBp(h.stats.attack, fx('briarStaff', h).attackBp as number), { pid: h.pid });
      featUse(s, h, 'briarStaff', back);
      ev(s, { t: 'beam', x: h.x, y: h.y, tx: from.x, ty: from.y, kind: 'briarBack' });
    }
  }
};

/* ---------- Pelliza del cabrero (Forma de lobo) ---------- */

/** Wolf form: shadow wolves run by the druid while it lasts. Aullido (its final form): a howl that stuns around. */
export const shadeWolves = (s: BattleState, h: Hero, ticks: number): void => {
  if (!hasUq(h, 'goatherdPelt')) return;
  const f = fx('goatherdPelt', h);
  const bots = (s.bots ??= []);
  s.bots = bots.filter((b) => b.kind !== 'shadeWolf' || b.owner !== h.id);
  const hp = mulBp(h.stats.maxHp, f.hpBp as number);
  for (let i = 0; i < (f.count as number); i++) {
    const a = h.facing + 256 + idiv(1024 * i, f.count as number);
    s.bots.push({
      id: s.nextId++, owner: h.id, pid: h.pid, kind: 'shadeWolf', x: h.x + idiv(cosA(a) * 500, 10000), y: h.y + idiv(sinA(a) * 500, 10000),
      hp, maxHp: hp, damage: mulBp(h.stats.attack, f.damageBp as number), radius: f.radius as number, every: f.every as number, cd: 0,
      expires: s.tick + ticks, taunt: 0, aura: 0, auraBp: 0, facing: h.facing, anim: 'idle', animT: 0, follow: true, uq: 'goatherdPelt',
    });
  }
  h.wolfKills = 0;
  ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'shadeWolves' });
  if (!formFinal(h, 'goatherdPelt')) return;
  const ff = formFx('goatherdPelt', 'final');
  for (const o of enemiesNear(s, h.x, h.y, ff.radius as number, (e) => live(s, e))) {
    const st = pieceStun(o, ff.stun as number);
    if (st > o.stun) o.stun = st;
  }
  ev(s, { t: 'boom', x: h.x, y: h.y, r: ff.radius as number, kind: 'howl' });
};

/* ---------- Peto del jardinero (Muro de espinos) ---------- */

/** The thorn wall just laid holds what crosses it, once each, and pricks `pinchBp` as hard. */
export const wallHoldOn = (s: BattleState, h: Hero): void => {
  if (!hasUq(h, 'gardenerPlate')) return;
  const z = lastZone(s, h, 'thorns');
  if (!z) return;
  z.uqz = 'gardenerPlate';
  z.uqh = h.id;
  z.seen = [];
  z.damage = mulBp(z.damage, fx('gardenerPlate', h).pinchBp as number);
};

const wallTick = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('gardenerPlate', h);
  const seen = (z.seen ??= []);
  const before = seen.length;
  const venom = formFinal(h, 'gardenerPlate') && z.ticks % z.every === 0 ? formFx('gardenerPlate', 'final') : null;
  for (const e of s.enemies) {
    if (e.hp <= 0 || !targetable(s, e)) continue;
    if (!z.cells.includes(cellAtWorld(s, e.x, e.y))) continue;
    if (!seen.includes(e.id)) {
      seen.push(e.id);
      hold(e, f.hold as number);
    }
    // Espinas envenenadas: what the thorns prick is poisoned (ready for the burst of fire).
    if (venom && !isFlying(e) && z.damage > 0) poison(e, z.damage, venom.every as number, venom.ticks as number, z.pid);
  }
  if (seen.length > before) ev(s, { t: 'boom', x: z.x, y: z.y, r: 500, kind: 'thornHold' });
  featUse(s, h, 'gardenerPlate', seen.length - before);
  if (before < FEAT.need.wallHeld && seen.length >= FEAT.need.wallHeld) featPieceTest(s, h, 'gardenerPlate');
};

/* ---------- Colgante del enterrador (Plaga) ---------- */

/** The plague just laid: what it poisons passes it on. */
export const contagionOn = (s: BattleState, h: Hero): void => {
  if (!hasUq(h, 'undertakerPendant')) return;
  const z = lastZone(s, h, 'plague');
  if (!z) return;
  z.uqz = 'undertakerPendant';
  z.uqh = h.id;
  z.seen = [];
};

const contagionTick = (s: BattleState, z: Zone, h: Hero): void => {
  const f = fx('undertakerPendant', h);
  const seen = (z.seen ??= []);
  if (z.ticks % z.every === 0) seenIn(s, z, h, 'undertakerPendant', FEAT.need.plague);
  // Nube negra (its final form): what the plague poisoned hits less while it stays poisoned (pegan menos; the big ones and the elites at half).
  if (formFinal(h, 'undertakerPendant')) {
    const ff = formFx('undertakerPendant', 'final');
    for (const id of seen) {
      const o = s.enemies.find((e) => e.id === id);
      if (!o || o.hp <= 0 || !poisoned(o)) continue;
      const bp = o.elite || stunFor({ ...o, elite: false }, 3) === 0 ? 10000 - idiv(10000 - (ff.bp as number), 2) : (ff.bp as number);
      if ((o.blind ?? 0) < (ff.ticks as number)) o.blind = ff.ticks as number;
      o.blindBp = imin(o.blindBp ?? 10000, bp);
    }
  }
  if (z.ticks % (f.every as number) !== 0 || !seen.length) return;
  // The ones carrying it: poisoned, by this plague.
  const carriers: Enemy[] = [];
  for (const e of s.enemies) if (e.hp > 0 && poisoned(e) && seen.includes(e.id)) carriers.push(e);
  if (!carriers.length) return;
  const reach = f.reach as number;
  const dmg = mulBp(z.damage, f.spreadBp as number);
  const before = seen.length;
  let n = 0;
  for (const o of s.enemies) {
    if (n >= SPREAD_PER_PULSE) break;
    if (!live(s, o) || poisoned(o) || seen.includes(o.id)) continue;
    const by = carriers.find((c) => withinRange(c.x, c.y, o.x, o.y, reach));
    if (!by) continue;
    poison(o, dmg, SKILL_FX.plague.every, SKILL_FX.plague.ticks, z.pid);
    seen.push(o.id);
    n++;
    ev(s, { t: 'beam', x: by.x, y: by.y, tx: o.x, ty: o.y, kind: 'contagion' });
  }
  featUse(s, h, 'undertakerPendant', n);
  if (before < FEAT.need.plague && seen.length >= FEAT.need.plague) featPieceTest(s, h, 'undertakerPendant');
};

/* ---------- Piedra del suelo (Temblor de raíces) ---------- */

/** The quake opens cracks that run out from the edge of its circle (`radius`), each step striking and stunning what they cross. */
export const cracksOn = (s: BattleState, h: Hero, p: { x: number; y: number } | null, radius: number): void => {
  if (!p || !hasUq(h, 'groundStone')) return;
  const f = fx('groundStone', h);
  const q = s.nextId++;
  h.crackQ = q;
  h.crackN = 0;
  const count = imin(f.count as number, CRACKS_MAX);
  const a0 = p.x === h.x && p.y === h.y ? h.facing : iatan2(p.y - h.y, p.x - h.x);
  for (let i = 0; i < count; i++) {
    const a = (a0 + idiv(1024 * i, count)) % 1024;
    due(s, { at: s.tick + 1, k: 'crack', hid: h.id, x: p.x, y: p.y, n: [a, radius + (f.step as number), q, radius + (f.length as number)] });
  }
  // Suelo que no perdona (its final form): the quake's ground leaves what stands on it without defence.
  if (formFinal(h, 'groundStone')) {
    const z = lastZone(s, h, 'quake');
    if (z) {
      z.uqz = 'groundStone';
      z.uqh = h.id;
    }
  }
};

const crack = (s: BattleState, h: Hero, d: UqDue): void => {
  const f = fx('groundStone', h);
  const [a = 0, run = 0, q = 0, end = 0] = d.n ?? [];
  const x = d.x + idiv(cosA(a) * run, 10000);
  const y = d.y + idiv(sinA(a) * run, 10000);
  let n = 0;
  for (const o of enemiesNear(s, x, y, f.width as number, (e) => live(s, e) && !isFlying(e) && e.crackBy !== q)) {
    o.crackBy = q;
    damageEnemy(s, o, f.damage as number, { pid: h.pid });
    const st = pieceStun(o, f.stun as number);
    if (st > o.stun) o.stun = st;
    if (st > 0) n++;
  }
  ev(s, { t: 'boom', x, y, r: f.width as number, kind: 'crack' });
  featUse(s, h, 'groundStone', n);
  if (h.crackQ === q) {
    const before = h.crackN ?? 0;
    h.crackN = before + n;
    if (before < FEAT.need.cracks && h.crackN >= FEAT.need.cracks) featPieceTest(s, h, 'groundStone');
  }
  if (run + (f.step as number) <= end) due(s, { ...d, at: s.tick + 1, n: [a, run + (f.step as number), q, end] });
};

const brokenGround = (s: BattleState, z: Zone): void => {
  const bp = formFx('groundStone', 'final').takenBp as number;
  for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => targetable(s, o) && !isFlying(o))) {
    e.rawBp = bp;
    e.rawUntil = s.tick + 2;
  }
};

/* ---------- Bellota del huerto (Despertar del bosque) ---------- */

/** The trees just woken hold what they strike, and strike `damageBp` as hard. Árboles que te siguen: they walk with the druid. */
export const treeHoldOn = (s: BattleState, h: Hero): void => {
  if (!hasUq(h, 'orchardAcorn')) return;
  const f = fx('orchardAcorn', h);
  const q = s.nextId++;
  h.treeQ = q;
  h.treeN = 0;
  const follow = formFinal(h, 'orchardAcorn');
  for (const b of s.bots ?? []) {
    if (b.kind !== 'treant' || b.owner !== h.id || b.uq) continue;
    b.uq = 'orchardAcorn';
    b.uqq = q;
    b.damage = mulBp(b.damage, f.damageBp as number);
    if (follow) b.follow = true;
  }
};

/** A summon of the druid's panoply struck: the wolves count their blows and kills, the trees hold. */
export const druidBotHit = (s: BattleState, b: Bot, e: Enemy, dealt: number): void => {
  const h = heroById(s, b.owner);
  if (!h) return;
  if (b.uq === 'goatherdPelt') {
    featUse(s, h, 'goatherdPelt', dealt);
    if (e.hp <= 0) {
      h.wolfKills = (h.wolfKills ?? 0) + 1;
      if (h.wolfKills === FEAT.need.wolfKills) featPieceTest(s, h, 'goatherdPelt');
    }
    return;
  }
  if (b.uq !== 'orchardAcorn' || e.hp <= 0) return;
  const f = fx('orchardAcorn', h);
  if (e.holdAt !== undefined && s.tick - e.holdAt < (f.every as number)) return;
  e.holdAt = s.tick;
  hold(e, f.hold as number);
  featUse(s, h, 'orchardAcorn', 1);
  ev(s, { t: 'beam', x: b.x, y: b.y, tx: e.x, ty: e.y, kind: 'treeHold' });
  if (e.treeBy === b.uqq) return;
  e.treeBy = b.uqq;
  if (h.treeQ !== b.uqq) return;
  h.treeN = (h.treeN ?? 0) + 1;
  if (h.treeN === FEAT.need.trees) featPieceTest(s, h, 'orchardAcorn');
};

/* ---------- each tick ---------- */

/** A zone a druid's piece changed, every tick (from updateZones, after its own pulse), and when it ends. */
export const druidZone = (s: BattleState, z: Zone): void => {
  const h = heroById(s, z.uqh);
  if (!h) return;
  if (z.uqz === 'herbalistStaff') {
    if (z.ticks > 0) driftTick(s, z, h);
    else mushrooms(s, z, h);
  } else if (z.uqz === 'gardenerPlate') wallTick(s, z, h);
  else if (z.uqz === 'undertakerPendant') contagionTick(s, z, h);
  else if (z.uqz === 'groundStone') brokenGround(s, z);
};

/** The druid's kinds in the queue (battle/panoply.ts runs it): true when it was one. They run with the druid down too. */
export const druidDue = (s: BattleState, h: Hero, d: UqDue): boolean => {
  if (d.k === 'hivePulse') hivePulse(s, h, d);
  else if (d.k === 'crack') crack(s, h, d);
  else if (d.k === 'rootEnd') rootEnd(s, h, d);
  else return false;
  return true;
};

/** Sin defensa (§8): the broken ground's, against the acid's; the strongest stays. */
export const rawBp = (s: BattleState, e: Enemy, acid: number): number =>
  e.rawUntil !== undefined && e.rawUntil > s.tick && (e.rawBp ?? 0) > acid ? (e.rawBp as number) : acid;
